import type { AttachmentFit, Motion, TowerKit } from "../kit/types";
import { ROOFED, SLENDER } from "./towerGroups";

/**
 * Starfall — the rarest cosmetic in the game: a tower skin dropped by the Starfall Wyrm (1 wave in 1,000), one per tower
 * type (`tower.starfall_<tower>`, each worn only on its own tower; the ids are registered in kits/index.ts). Owner's
 * brief: "shiny and sparkly to represent how rare it is" — the most dazzling skin in the set (contract
 * tools/art/skins/README.md).
 *
 * The look: pearlescent opal masonry glittering with star flecks that twinkle, a prismatic lilac roof veined in
 * shimmering opal-cyan, pearl-lacquered galleries seamed in rose gold, rose-gold and platinum fittings with a star-glow
 * trim, and night-sky cloth sewn with twinkling stars. On top: a faceted star-crystal floating over the apex in a
 * platinum star-cradle, spinning and pulsing in rose, lilac and cyan, with a ring of prism shards counter-orbiting it;
 * two comet-tail pennants streaming from the eaves; crystal shards hovering and bobbing over every eave; opal star
 * sigils breathing light on the drum; prismatic crystal clusters on the plinth corners; and star glints twinkling all
 * over (detail tiers). In a level: rose and cyan sparkles burst off the star every 1.4–1.8 s, and every shot — not
 * one in a flourish gap — leaves in a sparkle burst and lands in a radiant prismatic one. Props: art/hq/skins/starfall/kit.glb (tools/art/skins/starfall.py).
 *
 * Readability: the dominant hues are pearl, lilac and rose gold (no element hue); the cyan is an accent in the veins
 * and the prism gradient only. The element parts (crystals, coils, flames, orb, mask) keep their own glow.
 *
 * Whole models (2026-10-21, §11): tiers 1–3 draw each tower's own Starfall model `tower_<id>_t<n>__starfall.glb`
 * (art/towerkit/tskin_starfall.py, the Starfall Citadel's materials and shapes): pearl-opal masonry, opaque deep-indigo
 * star-field roofs with champagne ribs, a smaller great star in turning champagne rings as the crown, bold star-veins
 * ending in twinkling nodes, star-crystal clusters, falling stars circling the tower; its own idle / fire / `show`
 * (the starfall reveal). Those models are the whole look (onModel: none). Tier IV (the branch models, stock geometry)
 * wears this kit as a big kit: the stock roof is hidden and the Citadel's star-field spire takes its place (on pearl
 * columns where the roof storey holds the bow, the brew or the beacon's orb), the star answers the shot (charge).
 */
/** The star's height over a roof's apex, and its scale by tier (the cradle and the orbit share it). */
const STAR_Y = 0.3;
const STAR_SCALE = [1.85, 2.1, 2.35] as const;
/** The star-crystal: a steady turn, a float and a bright breath; it swells and flares as each shot leaves. */
/**
 * The star flecks' glow on the stone and cloth of a stock-geometry tower (tier IV, and a skin worn mid-level before its
 * own models load). At 3.4–3.6 the flecks cover the pale pearl so densely that the bloom pass turned the whole drum into
 * a white column in a level; this keeps them twinkling stars.
 */
const STAR_FLECK_GLOW = 1.1;
/** The pearl paint's roughness on stock geometry: glossier and a pale drum is one sheet of key light under the bloom. */
const STAR_STONE_ROUGHNESS = 0.42;
/** The pearl paint's base gain: already-pale stone (the beacon's) passed white at 1.24; darker towers get PEARL_GAIN. */
const STAR_STONE_GAIN = 0.9;

const STAR_MOTION: Motion = { spin: 1.1, bob: 0.02, pulse: 0.07, glow: 0.55, hz: 0.45, charge: { glow: 1.6, pulse: 0.12 } };
/** The roof storeys that hold the weapon or the element (bow, brew, the beacon's orb): the spire stands on columns there. */
const PAVILIONS = ["archer", "poison", "beacon"];
const SPIRED = [...ROOFED, "arcane"].filter((t) => !PAVILIONS.includes(t));
const SPIRE_MARGIN = 0.05;
/** The barracks' roof envelope is its hall plus a turret pole: the spire is fitted to the hall's box (as Bronze's dome). */
const BARRACKS_SPIRE: AttachmentFit = { fitTo: { to: "roof", shape: "box", margin: 0.5 }, scale: 0.65 };
/** The beacon's orb stands in its roof's upper half: its pavilion rises this far, metres. */
const BEACON_RISE = 0.27;
/** The spire's stars brighten as the shot nears. */
const SPIRE_MOTION: Motion = { glow: 0.35, hz: 0.4, charge: { glow: 2.2 } };
/** The prism shards counter-orbit the star a little faster. */
const ORBIT_MOTION: Motion = { spin: -1.7, bob: 0.012, glow: 0.5, hz: 0.6 };
/** The arcane spire's star floats over its own floating crystal (clear of the rune rings); the hex totem's over its feathers. */
const ARCANE_STAR_Y = 0.68;
const HEX_STAR_Y = 0.42;
/** Frost: no roof; the element crystal rises 0.23 (t1), 0.30 (t2) and 0.74 m (t3) over the battlement — the star floats above it. */
const FROST_STAR_Y = 0.5;
const FROST_T3_STAR_Y = 0.98;
/** The beacon's apex is just over its orb cage: its cradle and star stand a little higher, clear of the orb's glow. */
const BEACON_LIFT = 0.3;

export const STARFALL: TowerKit = {
  id: "tower.starfall",
  asset: "skins/starfall/kit",
  model: "starfall",
  hide: ["roof"],
  // the hex totem keeps its feather crown: its mask stands inside it
  hideFit: { hex: [] },
  tier4: true,
  slots: {
    // pearl-white opal: glossy, a faint lilac cast, rose-gold trim on the carved bands, and star flecks that twinkle
    stone: { tint: 0xf8ecf8, mix: 0.9, gain: STAR_STONE_GAIN, keepAccents: false, roughness: STAR_STONE_ROUGHNESS, metalness: 0.12, trim: { colour: 0xffc6e4, strength: 0.75 }, pattern: { kind: "flecks", colour: 0xffd8f6, scale: 46, strength: 0.65, glow: STAR_FLECK_GLOW } },
    // prismatic roof: lilac crystal tiles veined in opal-cyan light that shimmers
    roof: { tint: 0xb58ef6, mix: 0.94, gain: 1.75, keepAccents: false, roughness: 0.16, metalness: 0.12, trim: { colour: 0xffd2f2, strength: 1.0 }, pattern: { kind: "veins", colour: 0xffc2ec, scale: 5.5, strength: 0.7, glow: 1.5 } },
    // pearl lacquer seamed in rose gold
    wood: { tint: 0xecdcf2, mix: 0.9, gain: 1.3, keepAccents: false, roughness: 0.22, metalness: 0.2, trim: { colour: 0xffcfb0, strength: 0.6 }, pattern: { kind: "veins", colour: 0xf2b49a, scale: 7, strength: 0.55, glow: 0.5 } },
    // rose gold and platinum with a star-glow trim
    metal: { tint: 0xf6cdbd, mix: 0.92, gain: 1.2, metalness: 1, roughness: 0.14, keepAccents: false, trim: { colour: 0xfff0fa, strength: 1.0 }, pattern: { kind: "hammered", colour: 0xffe8f4, scale: 26, strength: 0.3 } },
    // night-sky cloth sewn with twinkling stars
    cloth: { tint: 0x342c78, mix: 0.92, gain: 0.95, roughness: 0.5, keepAccents: false, trim: { colour: 0xffd6f2, strength: 0.7 }, pattern: { kind: "flecks", colour: 0xffffff, scale: 40, strength: 1, glow: STAR_FLECK_GLOW } },
  },
  attachments: [
    // big kit (tier IV, or a tier whose model is missing): the Citadel's star-field spire in the hidden roof's place,
    // on pearl columns over the bow / brew / orb
    { piece: "skyspire", at: [], towers: SPIRED, ifHidden: "roof", fitTo: { to: "roof", margin: SPIRE_MARGIN }, motion: SPIRE_MOTION, fit: { barracks: BARRACKS_SPIRE, fire: { fitTo: { to: "roof", height: "keep", margin: 0.5 } } } },
    { piece: "skypav", at: [], towers: PAVILIONS, ifHidden: "roof", fitTo: { to: "roof", margin: SPIRE_MARGIN }, motion: SPIRE_MOTION, fit: { beacon: { offset: [0, BEACON_RISE, 0] } } },
    // the platinum star-cradle just under the apex point; the arcane spire's floats under its star (its collar is
    // ringed by the crystal's rune rings), the hex totem wears it as a collar round the pole's top under the mask, and
    // on frost it rings the crystal's foot on the battlement
    { piece: "cradle", at: ["apex"], offset: [0, -0.06, 0], scale: [1.55, 1.75, 1.95], fit: {
      frost: { offset: [0, -0.3, 0], scale: [1.9, 2.1, 2.3] },
      beacon: { offset: [0, BEACON_LIFT, 0], scale: [1.15, 1.25, 1.35] },
      arcane: { offset: [0, ARCANE_STAR_Y - 0.17, 0], scale: [1.2, 1.3, 1.4] },
      hex: { at: ["top"], offset: [0, -0.1, 0], scale: [1.6, 1.7, 1.8] },
    } },
    // the faceted star-crystal: the silhouette at game distance
    { piece: "star", at: ["apex"], towers: [...ROOFED, ...SLENDER], offset: [0, STAR_Y, 0], scale: STAR_SCALE, motion: STAR_MOTION, fit: {
      beacon: { offset: [0, STAR_Y + BEACON_LIFT, 0] },
      arcane: { offset: [0, ARCANE_STAR_Y, 0], scale: [1.3, 1.4, 1.5] },
      hex: { offset: [0, HEX_STAR_Y, 0], scale: [1.3, 1.45, 1.6] },
    } },
    { piece: "star", at: ["apex"], towers: ["frost"], tiers: [1, 2], offset: [0, FROST_STAR_Y, 0], scale: [1.5, 1.65, 1.8], motion: STAR_MOTION },
    { piece: "star", at: ["apex"], towers: ["frost"], tiers: [3], offset: [0, FROST_T3_STAR_Y, 0], scale: [1.8, 1.8, 1.8], motion: STAR_MOTION },
    // the prism shards counter-orbiting the star (frost: round the crystal itself)
    { piece: "orbit", at: ["apex"], offset: [0, STAR_Y, 0], scale: STAR_SCALE, motion: ORBIT_MOTION, fit: {
      frost: { offset: [0, 0.16, 0], scale: [1.9, 2.0, 2.2] },
      beacon: { offset: [0, STAR_Y + BEACON_LIFT, 0] },
      arcane: { offset: [0, ARCANE_STAR_Y, 0], scale: [1.3, 1.4, 1.5] },
      hex: { offset: [0, HEX_STAR_Y, 0], scale: [1.3, 1.45, 1.6] },
    } },
    // a comet streaming out from every eave, its tail flying radially away from the tower (turned so its right runs
    // out of the wall: the flank pair is face-on to the game camera), with a prism shard hovering over each pole as
    // the comet's head (frost and the slender shafts fly them from the upper band; the mortar's back eave, crest1,
    // would cross its barrel)
    { piece: "comet", at: ["crest"], offset: [0, 0, 0.02], yaw: -90, scale: [1.1, 1.25, 1.4], motion: { wave: 0.035, waveK: 16, sway: 3, hz: 0.5 }, fit: {
      frost: { at: ["band"] },
      beacon: { at: ["band"] },
      arcane: { at: ["band"], scale: [0.9, 1.0, 1.1] },
      hex: { at: ["band"], scale: [0.9, 1.0, 1.1] },
      mortar: { at: ["crest0", "crest2", "crest3"] },
    } },
    { piece: "spark", at: ["crest"], offset: [0, 0.42, 0.03], scale: [1.2, 1.35, 1.5], motion: { spin: 1.2, bob: 0.03, glow: 0.6, hz: 0.5 }, fit: {
      frost: { at: ["band"], offset: [0, 0.4, 0.03] },
      beacon: { at: ["band"], offset: [0, 0.4, 0.03] },
      arcane: { at: ["band"], offset: [0, 0.36, 0.03], scale: [1.0, 1.1, 1.2] },
      hex: { at: ["band"], offset: [0, 0.36, 0.03], scale: [1.0, 1.1, 1.2] },
      mortar: { at: ["crest0", "crest2", "crest3"] },
    } },
    { piece: "sigil", at: ["wall"], offset: [0, 0, 0.006], scale: [1.45, 1.6, 1.75], motion: { glow: 0.7, hz: 0.55 }, fit: { arcane: { scale: [1.05, 1.15, 1.25] }, hex: { scale: [0.95, 1.05, 1.15] } } },
    { piece: "cluster", at: ["corner"], scale: [1.3, 1.45, 1.6], motion: { glow: 0.6, hz: 0.4 } },
    // a little constellation of three sparkles twinkling by the star (detail tiers)
    { piece: "glint", at: ["apex"], offset: [0.16, STAR_Y + 0.17, 0.06], scale: 1.5, detail: true, motion: { twinkle: 0.55, spin: 1.4 }, fit: {
      frost: { offset: [0.18, 0.62, 0.06] },
      beacon: { offset: [0.16, STAR_Y + BEACON_LIFT + 0.17, 0.06] },
      arcane: { offset: [0.2, ARCANE_STAR_Y + 0.16, 0.06] },
      hex: { offset: [0.16, HEX_STAR_Y + 0.16, 0.06] },
    } },
  ],
  // rose sparkles rising off the star with cyan glints flashing in place: a prismatic burst, as often as the budget
  // allows (KIT_BUDGET's 1.4 s floor) and packed tight round the star
  idle: { kind: "sparkles", colour: 0xffc4ec, every: [1.4, 1.8], at: "apex", lift: STAR_Y, count: 3, radius: 0.15,
    also: { kind: "glints", colour: 0xb4f4ff, count: 3, radius: 0.2 },
    fit: { frost: { lift: FROST_STAR_Y }, beacon: { lift: STAR_Y + BEACON_LIFT }, arcane: { lift: ARCANE_STAR_Y }, hex: { lift: HEX_STAR_Y } } },
  // every shot leaves in a sparkle burst and lands in a radiant prismatic one (everyShot: no flourish gap, see Flourish)
  flourish: { impact: { kind: "radiant", colour: 0xf2c4ff, count: 8 }, muzzle: { colour: 0xfff0fc, count: 6 }, everyShot: true },
  preview: { tower: "archer", tier: 3 },
};

/**
 * Pearl on every tower: the palette swap keeps a texture's own light and dark, so the towers painted dark (the fire
 * tower's soot-black brick, the ballista's tarred timber, the lightning and mortar drums, the barracks' slate roof)
 * came out as dark starry stone. Each Starfall skin is worn by one tower type only, so its kit lifts those slots by
 * the tower's own measure: gain = target ÷ the slot's mean painted luma over t1–t3 (texels sampled at the parts'
 * vertices, ÷ SkinMaterials.RefLuma), targets stone 0.78, wood 0.7, roof 0.86, metal 0.6 — what the archer's pearl
 * drum and lilac roof come to — clamped to [the kit's own gain, 3.6]. Towers already at least that pale keep the kit.
 */
const PEARL_GAIN: Readonly<Record<string, Partial<Record<"stone" | "wood" | "roof" | "metal", number>>>> = {
  cannon: { wood: 1.49, roof: 3.19 },
  fire: { stone: 3.4, wood: 1.93, roof: 2.37 },
  lightning: { stone: 2.17, wood: 1.93, roof: 1.9 },
  ballista: { stone: 3.34, wood: 3.18, roof: 3.6, metal: 2.8 },
  poison: { stone: 1.73 },
  mortar: { stone: 1.87, wood: 1.98, roof: 3.07 },
  barracks: { stone: 1.76, roof: 3.6 },
  hex: { wood: 1.47 },
};

/** The kit's slots with one tower's pearl gains. */
function pearlSlots(tower: string): TowerKit["slots"] {
  const g = PEARL_GAIN[tower];
  if (!g) return STARFALL.slots;
  const slots: Record<string, TowerKit["slots"][keyof TowerKit["slots"]]> = { ...STARFALL.slots };
  for (const [slot, gain] of Object.entries(g)) slots[slot] = { ...STARFALL.slots[slot as keyof typeof g], gain };
  return slots as TowerKit["slots"];
}

/** The twelve Starfall skins: one shared kit, worn each on its own tower type (shop.json `tower.starfall_<tower>`). */
export const STARFALL_TOWERS = ["archer", "cannon", "frost", "fire", "lightning", "ballista", "poison", "beacon", "mortar", "barracks", "arcane", "hex"] as const;
export const STARFALL_KITS: readonly TowerKit[] = STARFALL_TOWERS.map((tower) => ({ ...STARFALL, id: `tower.starfall_${tower}`, slots: pearlSlots(tower), preview: { tower, tier: 3 } }));
